import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Progress } from '@/app/components/ui/progress';
import { Badge } from '@/app/components/ui/badge';
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  Loader2,
  Rocket,
  Building2,
  UserPlus,
  Shield,
  HardHat,
  FolderKanban,
  DollarSign,
  MapPin,
  LifeBuoy,
} from 'lucide-react';
import {
  useConfirmOnboardingTimeZone,
  useOnboardingState,
  useSkipOnboarding,
  useUpdateOnboardingSteps,
} from '@/hooks/use-onboarding';
import { useAuth } from '@/contexts/auth-context';
import { usePermissions } from '@/hooks/use-permissions';
import type { OnboardingStep } from '@/lib/types';
import { getModuleDisplayName } from '@/lib/modules';
import { ModulePickerStep } from './module-picker-step';
import { StarterContentStep } from './starter-content-step';
import { OrgTypeStep } from './org-type-step';
import { TimeZoneConfirmationStep } from './time-zone-confirmation-step';
import { TechProfileForm } from '@/app/pages/crew/techs/tech-profile-form';

type WizardStep = 'org-type' | 'module-picker' | 'checklist' | 'starter-content';

const STEP_ICONS: Record<string, React.ReactNode> = {
  company_profile: <Building2 className="h-5 w-5" />,
  first_employee: <UserPlus className="h-5 w-5" />,
  safety_first_jsa: <Shield className="h-5 w-5" />,
  crew_first_tech: <HardHat className="h-5 w-5" />,
  first_location: <MapPin className="h-5 w-5" />,
  projects_first_project: <FolderKanban className="h-5 w-5" />,
  safety_rescue_plan: <LifeBuoy className="h-5 w-5" />,
  payroll_settings: <DollarSign className="h-5 w-5" />,
};

function TechStepDetails({ step }: { step: OnboardingStep }) {
  const [showForm, setShowForm] = useState(false);
  const { canCreateTechs } = usePermissions();
  const techCount = step.record_count ?? 0;

  if (techCount > 0) {
    return (
      <p className="mt-1 text-sm text-gray-600">
        You have {techCount} {techCount === 1 ? 'technician' : 'technicians'} —{' '}
        <Link to="/crew/techs" className="font-medium text-blue-600 hover:underline">
          add more in Crew
        </Link>
      </p>
    );
  }

  if (step.completed || !canCreateTechs) return null;

  return (
    <div className="mt-3">
      {showForm ? (
        <TechProfileForm
          onCreated={() => setShowForm(false)}
          cancelAction={
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          }
        />
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(true)}>
          Add a technician here
        </Button>
      )}
    </div>
  );
}

function StepCard({
  step,
  onToggle,
  isUpdating,
}: {
  step: OnboardingStep;
  onToggle: (key: string, completed: boolean) => void;
  isUpdating: boolean;
}) {
  // A step backed by a real record stays done — unticking it would not
  // remove the record.
  const lockedDone = step.completed && !!step.auto_completed;
  const isTechStep = step.key === 'crew_first_tech';
  const hasTechs = isTechStep && (step.record_count ?? 0) > 0;

  return (
    <div
      className={`flex items-start gap-4 rounded-lg border p-4 transition-colors ${
        step.completed
          ? 'border-green-200 bg-green-50/50'
          : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(step.key, !step.completed)}
        disabled={isUpdating || lockedDone}
        title={lockedDone ? 'Done — found in your records' : undefined}
        className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-blue-600 disabled:opacity-50"
        aria-label={step.completed ? `Mark "${step.title}" as incomplete` : `Mark "${step.title}" as complete`}
      >
        {step.completed ? (
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        ) : (
          <Circle className="h-6 w-6" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">
            {STEP_ICONS[step.key] ?? <Circle className="h-5 w-5" />}
          </span>
          <h3
            className={`font-medium ${
              step.completed ? 'text-gray-500 line-through' : 'text-gray-900'
            }`}
          >
            {step.title}
          </h3>
          {step.module && (
            <Badge variant="secondary" className="text-xs">
              {getModuleDisplayName(step.module)}
            </Badge>
          )}
        </div>
        {!hasTechs && <p className="mt-1 text-sm text-gray-500">{step.description}</p>}
        {isTechStep && <TechStepDetails step={step} />}
      </div>

      {!step.completed && !isTechStep && (
        <Link
          to={step.link}
          className="flex-shrink-0 mt-0.5"
        >
          <Button variant="outline" size="sm" className="gap-1">
            Go <ArrowRight className="h-3 w-3" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function ChecklistStep({
  data,
  onAdvance,
}: {
  data: {
    onboarding_completed: boolean;
    steps: OnboardingStep[];
    completed_count: number;
    total_count: number;
    progress: number;
  };
  onAdvance: () => void;
}) {
  const navigate = useNavigate();
  const updateSteps = useUpdateOnboardingSteps();
  const skipOnboarding = useSkipOnboarding();

  const handleToggleStep = (key: string, completed: boolean) => {
    updateSteps.mutate({ steps: { [key]: completed } });
  };

  const handleFinishLater = async () => {
    try {
      await skipOnboarding.mutateAsync();
      navigate('/dashboard', { replace: true });
    } catch {
      return;
    }
  };

  const allDone = data.completed_count === data.total_count;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Rocket className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          Welcome to H3ight!
        </h1>
        <p className="mt-2 text-gray-500">
          Let&apos;s get your organization set up. Complete these steps to get the
          most out of the platform.
        </p>
      </div>

      {/* Progress */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-gray-700">Setup progress</span>
            <span className="text-gray-500">
              {data.completed_count} of {data.total_count} complete
            </span>
          </div>
          <Progress value={data.progress} className="h-3" />
          <p className="mt-1 text-right text-xs text-gray-400">{data.progress}%</p>
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup checklist</CardTitle>
          <CardDescription>
            Steps tick themselves off once the record exists. Every step is
            optional — click the circle to mark one done or skip it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.steps.map((step: OnboardingStep) => (
            <StepCard
              key={step.key}
              step={step}
              onToggle={handleToggleStep}
              isUpdating={updateSteps.isPending}
            />
          ))}
        </CardContent>
      </Card>

      {skipOnboarding.isError && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          Failed to skip setup. Please try again.
        </p>
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => void handleFinishLater()}
          disabled={skipOnboarding.isPending}
        >
          {skipOnboarding.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I&apos;ll finish this later
        </Button>

        <Button
          onClick={onAdvance}
          className={allDone ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          {allDone ? 'Next: Starter content' : 'Continue'}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function OnboardingPage() {
  // Steps complete from real records created on other pages, so always
  // re-check when the wizard opens instead of trusting the cached state.
  const { data, isLoading, isError } = useOnboardingState({ refetchOnMount: 'always' });
  const confirmTimeZone = useConfirmOnboardingTimeZone();
  const { user } = useAuth();

  const storageKey = user?.organization != null
    ? `h3_onboarding_step_${user.organization}`
    : null;

  const [wizardStep, setWizardStep] = useState<WizardStep>(() => {
    if (storageKey == null) return 'org-type';
    const saved = localStorage.getItem(storageKey);
    if (saved === 'starter-content') return 'starter-content';
    if (saved === 'checklist') return 'checklist';
    if (saved === 'module-picker') return 'module-picker';
    return 'org-type';
  });

  const advanceTo = (step: WizardStep) => {
    if (storageKey) localStorage.setItem(storageKey, step);
    setWizardStep(step);
  };

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            Unable to load onboarding state. Please try refreshing.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    data.is_admin
    && (data.time_zone_confirmation === 'pending' || data.time_zone_confirmation === 'provisioned')
  ) {
    return (
      <TimeZoneConfirmationStep
        confirmationState={data.time_zone_confirmation}
        savedTimeZone={data.time_zone}
        onConfirm={(timeZone) => confirmTimeZone.mutateAsync(timeZone).then(() => undefined)}
        isSaving={confirmTimeZone.isPending}
      />
    );
  }

  // Step 3: Starter content confirmation — shown after checklist
  if (wizardStep === 'starter-content') {
    const enabledModules = [...new Set(
      data.steps.filter(s => s.module).map(s => s.module as string)
    )];
    return (
      <StarterContentStep
        enabledModules={enabledModules}
        storageKey={storageKey}
      />
    );
  }

  // Step 1: Org type selection
  if (wizardStep === 'org-type') {
    return (
      <OrgTypeStep
        currentOrgType={data.org_type ?? null}
        onComplete={() => advanceTo('module-picker')}
      />
    );
  }

  // Step 2 (conditional): Module picker — only for bundle plans needing selection
  if (data.needs_module_selection && wizardStep === 'module-picker') {
    return (
      <ModulePickerStep
        availableModules={data.available_modules}
        selectedModules={data.selected_modules}
        onComplete={() => advanceTo('checklist')}
      />
    );
  }

  // Step 2: Setup checklist
  return (
    <ChecklistStep
      data={data}
      onAdvance={() => advanceTo('starter-content')}
    />
  );
}
